import React from "react";
import { Button } from "./Button";

interface PaginationProps {
 currentPage: number;
 totalPages: number;
 onPageChange: (page: number) => void;
 className?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
 currentPage,
 totalPages,
 onPageChange,
 className = "",
}) => {
 const getPageNumbers = () => {
  const pages: (number | string)[] = [];
  const maxVisible = 7;

  if (totalPages <= maxVisible) {
   // Show all pages
   for (let i = 1; i <= totalPages; i++) {
    pages.push(i);
   }
  } else {
   // Show first page
   pages.push(1);

   // Calculate start and end of middle section
   let start = Math.max(2, currentPage - 1);
   let end = Math.min(totalPages - 1, currentPage + 1);

   // Adjust if we're near the start
   if (currentPage <= 3) {
    end = Math.min(5, totalPages - 1);
   }

   // Adjust if we're near the end
   if (currentPage >= totalPages - 2) {
    start = Math.max(2, totalPages - 4);
   }

   // Add ellipsis before middle section if needed
   if (start > 2) {
    pages.push("ellipsis-start");
   }

   // Add middle pages
   for (let i = start; i <= end; i++) {
    pages.push(i);
   }

   // Add ellipsis after middle section if needed
   if (end < totalPages - 1) {
    pages.push("ellipsis-end");
   }

   // Show last page
   if (totalPages > 1) {
    pages.push(totalPages);
   }
  }

  return pages;
 };

 const pageNumbers = getPageNumbers();

 return (
  <div className={`flex items-center justify-center gap-2 ${className}`}>
   <Button
    variant="outline"
    size="sm"
    onClick={() => onPageChange(currentPage - 1)}
    disabled={currentPage === 1}
   >
    Previous
   </Button>

   <div className="flex items-center gap-1">
    {pageNumbers.map((page, index) => {
     if (page === "ellipsis-start" || page === "ellipsis-end") {
      return (
       <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
        ...
       </span>
      );
     }

     const pageNum = page as number;
     return (
      <button
       key={pageNum}
       onClick={() => onPageChange(pageNum)}
       className={`px-3 py-1 rounded ${
        currentPage === pageNum
         ? "bg-blue-600 text-white"
         : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-300"
       }`}
      >
       {pageNum}
      </button>
     );
    })}
   </div>

   <Button
    variant="outline"
    size="sm"
    onClick={() => onPageChange(currentPage + 1)}
    disabled={currentPage === totalPages}
   >
    Next
   </Button>
  </div>
 );
};

